/**
 * useFollowedRunners Hook
 * Manages followed runner bibs in localStorage, keyed by race slug
 */

import { useState, useCallback, useMemo, useEffect } from 'react';

const STORAGE_KEY_PREFIX = 'v2-followed-runners-';

function getStorageKey(raceSlug: string): string {
  return `${STORAGE_KEY_PREFIX}${raceSlug}`;
}

function loadFollowedBibs(raceSlug: string): string[] {
  try {
    const stored = localStorage.getItem(getStorageKey(raceSlug));
    if (stored) {
      return JSON.parse(stored);
    }
  } catch {
    // Ignore parse errors
  }
  return [];
}

function saveFollowedBibs(raceSlug: string, bibs: string[]): void {
  try {
    localStorage.setItem(getStorageKey(raceSlug), JSON.stringify(bibs));
  } catch {
    // Ignore storage errors
  }
}

export interface UseFollowedRunnersResult {
  followedBibs: Set<string>;
  isFollowed: (bib: string) => boolean;
  toggleFollow: (bib: string) => void;
  addFollow: (bib: string) => void;
  removeFollow: (bib: string) => void;
}

export function useFollowedRunners(raceSlug: string): UseFollowedRunnersResult {
  const [bibs, setBibs] = useState<string[]>(() => loadFollowedBibs(raceSlug));

  // Re-sync bibs state when raceSlug changes (e.g., navigating between races)
  // Without this, the component reuses stale bibs from the previous race
  useEffect(() => {
    setBibs(loadFollowedBibs(raceSlug));
  }, [raceSlug]);

  const followedBibs = useMemo(() => new Set(bibs), [bibs]);

  const isFollowed = useCallback(
    (bib: string) => followedBibs.has(bib),
    [followedBibs]
  );

  const toggleFollow = useCallback(
    (bib: string) => {
      setBibs((prev) => {
        const updated = prev.includes(bib)
          ? prev.filter((b) => b !== bib)
          : [...prev, bib];
        saveFollowedBibs(raceSlug, updated);
        return updated;
      });
    },
    [raceSlug]
  );

  const addFollow = useCallback(
    (bib: string) => {
      setBibs((prev) => {
        if (prev.includes(bib)) return prev;
        const updated = [...prev, bib];
        saveFollowedBibs(raceSlug, updated);
        return updated;
      });
    },
    [raceSlug]
  );

  const removeFollow = useCallback(
    (bib: string) => {
      setBibs((prev) => {
        const updated = prev.filter((b) => b !== bib);
        saveFollowedBibs(raceSlug, updated);
        return updated;
      });
    },
    [raceSlug]
  );

  return { followedBibs, isFollowed, toggleFollow, addFollow, removeFollow };
}
